const MARUTI_CAR_MAKER = "maruti";

export type SupplierAssociationLike = {
  supplierId?: string | null;
};

export type DigitalEstimateEligibilityInput = {
  carMaker?: string | null;
  supplierAssociations?: SupplierAssociationLike[] | null;
  associateSupplierIds?: string[] | null;
  hasSharedEstimateForEnquiry: boolean;
};

const normalizeId = (value: unknown): string => String(value ?? "").trim();

/**
 * Outer gate: customer is associated with any supplier in
 * `enforce_digital_estimate_associate_supplier_ids`.
 */
export function isCustomerAssociatedWithDigitalEstimateSuppliers(
  supplierAssociations: SupplierAssociationLike[] | null | undefined,
  associateSupplierIds: string[] | null | undefined,
): boolean {
  const configuredIds = (associateSupplierIds ?? [])
    .map(normalizeId)
    .filter(Boolean)
    .map((id) => id.toLowerCase());
  if (configuredIds.length === 0) {
    return false;
  }

  const configuredSet = new Set(configuredIds);
  return (supplierAssociations ?? []).some((association) => {
    const supplierId = normalizeId(association?.supplierId).toLowerCase();
    return Boolean(supplierId) && configuredSet.has(supplierId);
  });
}

export function isMarutiCarMaker(carMaker?: string | null): boolean {
  return normalizeId(carMaker).toLowerCase() === MARUTI_CAR_MAKER;
}

/**
 * True when an associated customer must push digital estimate before
 * Proceed / Hold / Cancel (Maruti + no SHARED for enquiry).
 */
export function requiresDigitalEstimateShare(
  input: DigitalEstimateEligibilityInput,
): boolean {
  if (
    !isCustomerAssociatedWithDigitalEstimateSuppliers(
      input.supplierAssociations,
      input.associateSupplierIds,
    )
  ) {
    return false;
  }
  if (!isMarutiCarMaker(input.carMaker)) {
    return false;
  }
  if (input.hasSharedEstimateForEnquiry) {
    return false;
  }
  return true;
}

/**
 * Push disable reason for associated customers only.
 * Returns empty string when Push should follow existing technical gates
 * (enforce-share state) or when the customer is not associated.
 *
 * If any estimate for the enquiry is already SHARED, that reason always wins
 * over Maruti / other digital-estimate messages.
 */
export function getDigitalEstimateIneligibilityReason(
  input: DigitalEstimateEligibilityInput,
): string {
  if (
    !isCustomerAssociatedWithDigitalEstimateSuppliers(
      input.supplierAssociations,
      input.associateSupplierIds,
    )
  ) {
    return "";
  }

  if (input.hasSharedEstimateForEnquiry) {
    return "An estimate was already shared for this enquiry";
  }
  if (!isMarutiCarMaker(input.carMaker)) {
    return "Digital estimate is only for Maruti car makers";
  }
  return "";
}

/** True when Proceed/Hold/Cancel must wait for a digital estimate push. */
export function isProceedBlockedByDigitalEstimate(
  input: DigitalEstimateEligibilityInput,
): boolean {
  return requiresDigitalEstimateShare(input);
}

export function hasSharedEstimateInList(
  estimates: Array<{ status?: string | null }> | null | undefined,
): boolean {
  return (estimates ?? []).some(
    (estimate) =>
      normalizeId(estimate?.status).toUpperCase() === "SHARED",
  );
}
